import {
  AG_GRID_LOCALE_DE,
  AG_GRID_LOCALE_EN,
  AG_GRID_LOCALE_FR,
} from '@ag-grid-community/locale';
import { UseFetchResult } from '@api/common';
import { createBrzThemes } from '@brz/brz-global-ag-grid-theme';
import Add from '@mui/icons-material/Add';
import Refresh from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { format, typedMemo } from '@utils';
import {
  CellFocusedEvent,
  ColDef,
  GridApi,
  RowNode,
  SelectionChangedEvent,
  themeQuartz,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { LoadingOverlay } from '../loading-overlay';
import { useMasterDetail } from '../master-detail';
import { Toolbar, ToolbarItem } from '../toolbar';
import { TooltipCellRenderer } from './TooltipCellRenderer';
import { TooltipHeaderRenderer } from './TooltipHeaderRenderer';

const { brzThemeWide } = createBrzThemes(themeQuartz);

interface ListViewProps<T> {
  rowData?: T[];
  columnDefs: ColDef[];
  onRowSelect?: (row: T) => void;
  showSearch?: boolean;
  toolbarItems?: ToolbarItem[];
  isLoading?: boolean;
  hideRefreshButton?: boolean;
  hideAddButton?: boolean;
  localizationKey?: string;
  idFieldName?: keyof T;
  readHook?: () => UseFetchResult<T[]>;
}

export const ListView = typedMemo(
  <T,>({
    rowData,
    columnDefs,
    onRowSelect,
    showSearch = true,
    toolbarItems = [],
    isLoading = false,
    hideRefreshButton,
    hideAddButton,
    localizationKey,
    idFieldName = 'id' as keyof T,
    readHook,
  }: Readonly<ListViewProps<T>>) => {
    const [filterText, setFilterText] = useState('');
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const masterDetailContext = useMasterDetail<T>();
    const gridApiRef = useRef<GridApi<T> | null>(null);

    const hookResult = readHook?.();
    const previousDataRef = useRef<T[] | null>(null);

    // Update context with hook data when available
    useEffect(() => {
      if (masterDetailContext && hookResult) {
        // Always update loading and error state
        masterDetailContext.loading.set(hookResult.loading);
        masterDetailContext.error.set(hookResult.error);

        // Set data only when hook data changes (new fetch result)
        if (
          hookResult.result &&
          hookResult.result !== previousDataRef.current
        ) {
          masterDetailContext.items.set(hookResult.result);
          previousDataRef.current = hookResult.result;
        }
      }
    }, [hookResult, masterDetailContext]);

    // Monkey Patch RowNode.setSelected to check canChangeSelection
    useEffect(() => {
      const originalSetSelected = RowNode.prototype.setSelected;

      RowNode.prototype.setSelected = function (
        selected: boolean,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...args: any[]
      ) {
        if (selected && masterDetailContext?.canChangeSelection) {
          masterDetailContext.canChangeSelection().then((canChange) => {
            if (canChange) {
              originalSetSelected.call(this, selected, ...args);
            }
          });
          return;
        }
        return originalSetSelected.call(this, selected, ...args);
      };

      return () => {
        RowNode.prototype.setSelected = originalSetSelected;
      };
    }, [masterDetailContext]);
    const effectiveRowData = useMemo(
      () => masterDetailContext?.items.value ?? rowData ?? [],
      [masterDetailContext?.items.value, rowData],
    );
    const effectiveLoading = masterDetailContext?.loading.value ?? isLoading;

    const defaultColDef = useMemo<ColDef>(
      () => ({
        cellRenderer: TooltipCellRenderer,
        headerComponent: TooltipHeaderRenderer,
      }),
      [],
    );

    const getLocaleText = useMemo(() => {
      switch (i18n.language) {
        case 'de':
          return AG_GRID_LOCALE_DE;
        case 'fr':
          return AG_GRID_LOCALE_FR;
        default:
          return AG_GRID_LOCALE_EN;
      }
    }, [i18n.language]);

    const rowSelection = useMemo(
      () => ({
        mode: 'singleRow' as const,
        checkboxes: false,
        enableClickSelection: true,
      }),
      [],
    );
    const onSelectionChanged = useCallback(
      (event: SelectionChangedEvent) => {
        const selectedRows = event.api.getSelectedRows();
        const selectedRow = selectedRows[0];
        if (selectedRow) {
          if (masterDetailContext?.selectedId) {
            const id = selectedRow[idFieldName];
            masterDetailContext.selectedId.set(id?.toString() ?? id);
          }
          if (onRowSelect) {
            onRowSelect(selectedRow);
          }
        }
      },
      [masterDetailContext, onRowSelect, idFieldName],
    );

    const onCellFocused = useCallback((event: CellFocusedEvent) => {
      if (event.rowIndex != null) {
        const rowNode = event.api.getDisplayedRowAtIndex(event.rowIndex);
        if (rowNode) {
          rowNode.setSelected(true, true);
        }
      }
    }, []);

    const onGridReady = useCallback((params: { api: GridApi<T> }) => {
      gridApiRef.current = params.api;
    }, []);

    /**Restore selection after refresh */
    useEffect(() => {
      if (
        gridApiRef.current &&
        masterDetailContext?.selectedId.value &&
        effectiveRowData.length > 0
      ) {
        const selectedId = masterDetailContext.selectedId.value;

        if (selectedId != null) {
          const rowIndex = effectiveRowData.findIndex(
            (row) => row[idFieldName]?.toString() === selectedId.toString(),
          );
          if (rowIndex >= 0) {
            const rowNode = gridApiRef.current.getDisplayedRowAtIndex(rowIndex);
            if (rowNode && !rowNode.isSelected()) {
              rowNode.setSelected(true);
            }
          } else {
            // Item not found in current data, clear selection
            masterDetailContext.selectedId.set(undefined);
          }
        }
      }
    }, [effectiveRowData, masterDetailContext, idFieldName]);

    const handleAddClick = useCallback(() => {
      navigate('new');
    }, [navigate]);

    const handleRefreshClick = useCallback(() => {
      if (hookResult?.refetch) {
        hookResult.refetch();
      } else if (masterDetailContext?.refetch) {
        masterDetailContext.refetch();
      }
    }, [hookResult, masterDetailContext]);

    const defaultToolbarItems: ToolbarItem[] = [];
    defaultToolbarItems.push({
      type: 'title',
      title: t(localizationKey + '.captionPlural'),
      align: 'left',
    });

    const standardToolbarItems = useMemo<ToolbarItem[]>(() => {
      const items: ToolbarItem[] = [];

      if (
        !hideRefreshButton &&
        (hookResult?.refetch || masterDetailContext?.refetch)
      ) {
        items.push({
          type: 'button',
          icon: <Refresh />,
          label: t('common.refresh'),
          onClick: handleRefreshClick,
          align: 'right',
        });
      }

      if (!hideAddButton) {
        items.push({
          type: 'button',
          icon: <Add />,
          label: t('common.new'),
          onClick: handleAddClick,
          align: 'right',
        });
      }

      return items;
    }, [
      hideRefreshButton,
      hideAddButton,
      handleRefreshClick,
      handleAddClick,
      masterDetailContext,
      hookResult?.refetch,
      t,
    ]);

    const allToolbarItems = [
      ...defaultToolbarItems,
      ...standardToolbarItems,
      ...toolbarItems,
    ];

    return (
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {allToolbarItems.length > 0 && <Toolbar items={allToolbarItems} />}
        {showSearch && (
          <TextField
            placeholder={format(
              t('listView.searchPlaceholder'),
              t(localizationKey + '.captionPlural'),
            )}
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            fullWidth
            sx={{ mb: 1, mt: 0 }}
          />
        )}
        <div className="ag-theme-brz" style={{ height: '100%', width: '100%' }}>
          <AgGridReact
            rowData={effectiveRowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            onSelectionChanged={onSelectionChanged}
            onCellFocused={onCellFocused}
            onGridReady={onGridReady}
            quickFilterText={filterText}
            rowSelection={rowSelection}
            animateRows={true}
            theme={brzThemeWide}
            localeText={getLocaleText}
            loading={effectiveLoading}
            loadingOverlayComponent={LoadingOverlay}
          />
        </div>
      </Box>
    );
  },
);
